/**
 * Shrink a picked image before it is uploaded.
 *
 * **The decoding happens here rather than on the server, and that is the
 * point.** `api/app/modules/identity/avatars.py` is deliberately written to
 * never allocate a pixel buffer — its docstring says so — because decoding
 * attacker-supplied images in the API process is the classic remote attack
 * surface. The browser already has a decoder, it is already sandboxed, it is
 * already holding the file, and the work lands on one person's device instead
 * of the shared API.
 *
 * The server still enforces `MAX_DIMENSION`. This is the courtesy; that is the
 * rule. A client that skips this is refused rather than served.
 */

/** Matches `avatars.MAX_DIMENSION`. Raising one without the other is a bug. */
export const MAX_AVATAR_DIMENSION = 512

/**
 * Only these three. `avatars.py` decides format by magic bytes and knows
 * exactly these, so producing anything else would make a file the server
 * refuses — and a canvas will happily encode to whatever is asked for.
 */
const ENCODABLE = {
  'image/png': 'image/png',
  'image/jpeg': 'image/jpeg',
  'image/webp': 'image/webp',
}

/**
 * Whether this file needs shrinking at all, given what it measures.
 *
 * Split out from the drawing so the decision is testable without a canvas —
 * jsdom has no real one, and a helper whose only logic sat behind
 * `getContext('2d')` would be a helper with no tests.
 */
export function fitWithin(width, height, max = MAX_AVATAR_DIMENSION) {
  const longest = Math.max(width, height)
  if (longest <= max) return null
  const scale = max / longest
  // Round rather than floor: flooring a 4032x3024 down can lose a pixel on the
  // short side and change the aspect ratio by a hair for no reason.
  return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

/**
 * Return `file` shrunk to fit, or `file` itself when it already does.
 *
 * Returning the original unchanged is deliberate rather than lazy. Re-encoding
 * a small PNG through a canvas can make it *larger*, and it would silently
 * convert formats — so a file that is already the right size is left exactly
 * as the person chose it.
 *
 * Anything unexpected — an undecodable file, a format the server would not
 * take, a browser without `createImageBitmap` — also returns the original. The
 * server does the validating, and it will say something useful; failing the
 * upload here would refuse a file with a worse message than the one that
 * already exists.
 */
export async function downscaleImage(file, max = MAX_AVATAR_DIMENSION) {
  const type = ENCODABLE[file?.type]
  if (!type || typeof createImageBitmap !== 'function') return file

  let bitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    return file
  }

  try {
    const target = fitWithin(bitmap.width, bitmap.height, max)
    if (!target) return file

    const canvas = document.createElement('canvas')
    canvas.width = target.width
    canvas.height = target.height
    const context = canvas.getContext('2d')
    if (!context) return file
    context.drawImage(bitmap, 0, 0, target.width, target.height)

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, 0.9))
    if (!blob) return file
    // Keep the name: the server does not read it, but a failed upload names
    // the file in the browser's own UI and "blob" is not a helpful thing to
    // see there.
    return new File([blob], file.name, { type })
  } finally {
    bitmap.close?.()
  }
}
